/**
 * XSS-safety helper for PMS/owner-supplied rich HTML rendered through
 * `dangerouslySetInnerHTML` in public widgets (#963).
 *
 * Property **descriptions** (summary, space, access, …) and the checkout
 * **rental-agreement** text are stored as raw HTML — a PMS sync writes whatever
 * upstream supplies straight to a `text` column, and Central serves it
 * unsanitized (the method literally named `sanitizePropertyDescriptions` is a
 * no-op). The widgets then inject it via `dangerouslySetInnerHTML`, so a value
 * like `<img src=x onerror=alert(1)>` or `<svg onload=…>` executes in every
 * visitor's browser. This is the sibling of #904 through a content/HTML channel
 * #904's CSS fix did not cover; the client is not a security boundary (a
 * compromised PMS account or sub-account editor is the injection source).
 *
 * These fields are INTENTIONALLY rich HTML (formatting, lists, links), so we
 * allowlist-sanitize — keep the safe formatting, strip everything executable —
 * rather than escape. Defense-in-depth: Central should sanitize on serve too
 * (routed to @shazzad), but this render-boundary guard makes the widgets safe
 * regardless of upstream, mirroring #904's approach.
 *
 * Uses `js-xss` (the `xss` package) — pure CommonJS with no ESM dependencies
 * (`commander` + `cssfilter`), so the same code bundles cleanly in the browser
 * widget IIFE (Vite), the renderer's SSR build (Next/webpack), and plain Node.
 * The description sink renders under SSR (`renderToString`), so isomorphic
 * behavior is required. `sanitize-html` was rejected here: it is CommonJS over
 * the ESM-only `htmlparser2`, which Next's webpack refuses to bundle
 * ("ESM packages need to be imported"), breaking renderer SSR for every page.
 */
// Import the default export, not named ones: js-xss is CommonJS with its helpers
// attached to `module.exports`, and cjs-module-lexer doesn't surface all of them
// as ESM named imports (a native-ESM `import { safeAttrValue }` throws). The
// default is always available across Vite, webpack, and Node ESM. `FilterXSS` is
// the only piece we need; attribute escaping is inlined below.
import xssModule from 'xss'
const { FilterXSS } = xssModule as unknown as typeof import('xss')

// URL-scheme allowlists. A relative URL (no scheme) is allowed; an explicit
// scheme must be on the list. Allowlisting (vs blocking `javascript:`) can't be
// bypassed by entity-/whitespace-obfuscating the scheme. `data:` is NOT allowed
// for images — js-xss's own default permits `data:image/...`, which covers the
// `data:image/svg+xml,<script>` vector, so we override src handling below.
const HREF_SCHEMES = ['http', 'https', 'mailto', 'tel']
const IMG_SCHEMES = ['http', 'https']

// HTML-escape an attribute value so a quote/angle-bracket can't break out of the
// quoted attribute (js-xss wraps returned values in double quotes).
function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function isSafeUrl(value: string, allowedSchemes: string[]): boolean {
  // Strip whitespace/control chars an attacker could splice into the scheme
  // (e.g. `java\tscript:` or a leading newline) before detecting it.
  // eslint-disable-next-line no-control-regex
  const stripped = value.replace(/[\x00-\x20]+/g, '')
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(stripped)
  if (!scheme) return true // relative URL or fragment — no scheme to abuse
  return allowedSchemes.includes(scheme[1].toLowerCase())
}

const filter = new FilterXSS({
  // Formatting, structure, lists, links, headings, tables, images — the tags
  // legitimate PMS description / rental-agreement rich text actually uses.
  // `a` deliberately omits rel/target: they're forced on afterwards (below).
  whiteList: {
    p: [], br: [], hr: [], span: [], div: [],
    b: [], strong: [], i: [], em: [], u: [], s: [], strike: [], del: [], ins: [],
    sub: [], sup: [], small: [], mark: [], abbr: ['title'],
    ul: [], ol: [], li: [], dl: [], dt: [], dd: [],
    a: ['href', 'title'],
    h1: [], h2: [], h3: [], h4: [], h5: [], h6: [],
    blockquote: [], pre: [], code: [],
    table: [], thead: [], tbody: [], tfoot: [], tr: [], caption: [],
    td: ['colspan', 'rowspan'], th: ['colspan', 'rowspan', 'scope'],
    colgroup: ['span'], col: ['span'],
    img: ['src', 'alt', 'title', 'width', 'height', 'loading'],
    figure: [], figcaption: [],
  },
  // Disallowed tags are removed outright (not escaped to visible text); script
  // and style have their text content dropped too. Non-allowlisted attributes
  // (every `on*` handler) are removed by js-xss's default.
  stripIgnoreTag: true,
  stripIgnoreTagBody: ['script', 'style'],
  safeAttrValue(tag, name, value) {
    if (name === 'href') return isSafeUrl(value, HREF_SCHEMES) ? escapeAttr(value.trim()) : ''
    if (tag === 'img' && name === 'src') return isSafeUrl(value, IMG_SCHEMES) ? escapeAttr(value.trim()) : ''
    // Other allowlisted attrs (title, alt, colspan, …) are plain text — escape
    // and keep. (No `style` attr is allowlisted, so no CSS filtering needed.)
    return escapeAttr(value.trim())
  },
})

// Force safe link behavior on every surviving <a> (js-xss keeps the tag; we add
// rel/target on top). Operates on already-sanitized HTML, and only touches
// anchors that lack the attribute so nothing is duplicated.
function hardenLinks(html: string): string {
  return html
    .replace(/<a\b(?![^>]*\brel=)/gi, '<a rel="noopener noreferrer nofollow"')
    .replace(/<a\b(?![^>]*\btarget=)/gi, '<a target="_blank"')
}

/**
 * Allowlist-sanitize an untrusted rich-HTML string for a `dangerouslySetInnerHTML`
 * sink. Returns "" for non-strings / empty input, otherwise the input with all
 * executable markup (scripts, event handlers, `javascript:`/`data:` URLs,
 * disallowed tags) stripped while safe formatting is preserved.
 */
export function safeHtml(value: unknown): string {
  if (typeof value !== 'string') return ''
  const v = value.trim()
  if (v === '') return ''
  return hardenLinks(filter.process(v))
}
